import { useEffect, useState } from "react";
import axios from "axios";
import { SkeletonList } from "./ReportSkeleton";
import {
  ArrowLeft, Crown, Sparkles, Loader2, Receipt, CalendarClock, XCircle,
  RefreshCw, CheckCircle2, Download, CreditCard, RotateCcw, ShoppingBag, Gift, Copy,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import GoogleButton from "./GoogleButton";
import BrandLogo from "./BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }) : "—");

const STATUS_BADGE = {
  paid: { text: "Pagado", cls: "bg-green-100 text-green-700" },
  pending: { text: "Pendiente", cls: "bg-yellow-100 text-yellow-700" },
  failed: { text: "Fallido", cls: "bg-red-100 text-red-700" },
  expired: { text: "Expirado", cls: "bg-gray-100 text-gray-500" },
};

const SUB_BADGE = {
  active: { text: "Activa", cls: "bg-green-100 text-green-700" },
  cancelled: { text: "Cancelada", cls: "bg-red-100 text-red-700" },
  expired: { text: "Expirada", cls: "bg-gray-100 text-gray-600" },
  manual: { text: "Asignación manual", cls: "bg-blue-100 text-blue-700" },
};

const InfoRow = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="flex items-start gap-3 bg-purple-50/60 rounded-xl px-4 py-3">
    <Icon className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
    <div>
      <p className="text-[11px] font-bold text-gray-400 uppercase">{label}</p>
      <p className="text-sm font-semibold text-gray-800">{value}</p>
    </div>
  </div>
);

export default function AccountPage() {
  const { user, authLoading, refreshUser } = useAuth();
  const [account, setAccount] = useState(null);
  const [payments, setPayments] = useState(null);
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);
  const [working, setWorking] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [notice, setNotice] = useState(null);

  const loadData = () => {
    Promise.all([
      axios.get(`${API}/account`),
      axios.get(`${API}/account/payments`),
      axios.get(`${API}/referral`),
    ])
      .then(([a, p, r]) => {
        setAccount(a.data);
        setPayments(p.data);
        setReferral(r.data);
      })
      .catch(() => {});
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referral.link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }).catch(() => {});
  };

  useEffect(() => {
    if (user) loadData();
     
  }, [user]);

  const doAction = async (path, errMsg) => {
    setWorking(true);
    try {
      const r = await axios.post(`${API}${path}`);
      setNotice(r.data.message);
      setConfirmCancel(false);
      refreshUser();
      loadData();
    } catch (err) {
      setNotice(err.response?.data?.detail || errMsg);
    } finally {
      setWorking(false);
    }
  };

  const isPro = account?.plan === "pro" || account?.plan === "enterprise";
  const cancelled = account?.subscription_status === "cancelled";
  const isManualPro = isPro && !account?.pro_until;
  const lastPaid = payments?.find((p) => p.payment_status === "paid");
  const subStatus = isManualPro
    ? SUB_BADGE.manual
    : SUB_BADGE[account?.subscription_status] || (isPro ? SUB_BADGE.active : null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-purple-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center"><BrandLogo size="sm" /></a>
          <a href="/" data-testid="account-back-link" className="flex items-center gap-2 text-sm font-semibold text-purple-700 hover:text-purple-900">
            <ArrowLeft className="w-4 h-4" /> Volver a la app
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6">
        {authLoading && <div data-testid="account-loading" className="py-6"><SkeletonList rows={4} /></div>}

        {!authLoading && !user && (
          <div data-testid="account-login-required" className="bg-white rounded-3xl shadow-xl p-10 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Tu cuenta</h2>
            <p className="text-gray-600 mb-6">Inicia sesión para ver tu plan, facturas y opciones.</p>
            <div className="flex justify-center"><GoogleButton /></div>
          </div>
        )}

        {user && (
          <>
            {/* Perfil */}
            <div data-testid="account-profile" className="bg-white rounded-3xl shadow-xl p-8 flex items-center gap-5">
              {user.picture ? (
                <img src={user.picture} alt={user.name} referrerPolicy="no-referrer" className="w-16 h-16 rounded-full border-4 border-purple-100" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white text-2xl font-bold">
                  {(user.name || user.email)[0].toUpperCase()}
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{user.name || "Mi cuenta"}</h1>
                <p className="text-sm text-gray-500">{user.email}</p>
                {account?.created_at && <p className="text-xs text-gray-400 mt-1">Miembro desde {fmtDate(account.created_at)}</p>}
              </div>
            </div>

            {/* Centro de suscripción */}
            <div data-testid="account-plan-card" className="bg-white rounded-3xl shadow-xl p-8">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isPro ? "bg-gradient-to-br from-yellow-400 to-orange-500" : "bg-purple-100"}`}>
                    {isPro ? <Crown className="w-6 h-6 text-white" /> : <Sparkles className="w-6 h-6 text-purple-600" />}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase">Plan activo</p>
                    <p data-testid="account-plan-name" className="text-xl font-bold text-gray-900 flex items-center gap-2">
                      {account?.plan === "pro" ? "Pro" : account?.plan === "enterprise" ? "Enterprise" : "Gratuito"}
                      {subStatus && (
                        <span data-testid="account-sub-status" className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${subStatus.cls}`}>{subStatus.text}</span>
                      )}
                    </p>
                  </div>
                </div>
                {account?.plan === "free" && (
                  <div className="text-right">
                    <span data-testid="account-remaining" className="text-sm font-bold bg-purple-100 text-purple-700 px-4 py-2 rounded-full">
                      Análisis restantes: {account.remaining}/{account.free_limit}
                    </span>
                    {(account.purchased_credits > 0 || account.referral_bonus > 0) && (
                      <p data-testid="account-credits-detail" className="text-xs text-gray-400 mt-1.5">
                        3 gratis{account.purchased_credits > 0 && ` + ${account.purchased_credits} comprados`}{account.referral_bonus > 0 && ` + ${account.referral_bonus} por referidos`}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {isPro && (
                <div className="grid sm:grid-cols-2 gap-3 mb-5">
                  {lastPaid && (
                    <InfoRow icon={ShoppingBag} label="Fecha de compra" value={fmtDate(lastPaid.created_at)} testid="account-purchase-date" />
                  )}
                  <InfoRow
                    icon={CalendarClock}
                    label={cancelled ? "Acceso Pro hasta" : "Vence / renovación"}
                    value={account?.pro_until ? fmtDate(account.pro_until) : "Sin fecha de expiración"}
                    testid="account-renewal"
                  />
                  <InfoRow
                    icon={RefreshCw}
                    label="Próximo cobro"
                    value={cancelled ? "Ninguno (plan cancelado)" : isManualPro ? "No aplica" : "Renovación manual · 7,99 $/mes"}
                    testid="account-next-charge"
                  />
                  <InfoRow icon={CreditCard} label="Método de pago" value="Tarjeta · Stripe Checkout" testid="account-payment-method" />
                </div>
              )}

              {isPro && cancelled && account?.pro_until && (
                <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-4">
                  <XCircle className="w-4 h-4 flex-shrink-0" />
                  Plan cancelado — mantienes el acceso Pro hasta el <b>{fmtDate(account.pro_until)}</b> y no se renovará.
                </div>
              )}

              {notice && (
                <div data-testid="account-notice" className="flex items-center gap-2 text-sm bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 mb-4">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> {notice}
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                <a
                  href="/precios"
                  data-testid="account-change-plan"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-md transition-all"
                >
                  <RefreshCw className="w-4 h-4" /> {isPro ? "Cambiar / renovar plan" : "Mejorar a Pro"}
                </a>

                {cancelled && account?.pro_until && (
                  <button
                    data-testid="account-reactivate-plan"
                    onClick={() => doAction("/account/reactivate", "No se pudo reactivar el plan.")}
                    disabled={working}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-60 shadow-md transition-all"
                  >
                    <RotateCcw className="w-4 h-4" /> {working ? "Reactivando..." : "Reactivar Plan Pro"}
                  </button>
                )}

                {account?.plan === "pro" && !cancelled && (
                  !confirmCancel ? (
                    <button
                      data-testid="account-cancel-plan"
                      onClick={() => setConfirmCancel(true)}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 transition-all"
                    >
                      <XCircle className="w-4 h-4" /> Cancelar Plan Pro
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        data-testid="account-cancel-confirm"
                        onClick={() => doAction("/account/cancel", "No se pudo cancelar el plan.")}
                        disabled={working}
                        className="px-4 py-2.5 rounded-xl font-semibold text-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 transition-all"
                      >
                        {working ? "Cancelando..." : "Sí, cancelar"}
                      </button>
                      <button
                        data-testid="account-cancel-abort"
                        onClick={() => setConfirmCancel(false)}
                        className="px-4 py-2.5 rounded-xl font-semibold text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all"
                      >
                        No, mantener
                      </button>
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Programa de referidos */}
            {referral?.enabled && (
              <div data-testid="account-referral" className="bg-white rounded-3xl shadow-xl p-8">
                <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <Gift className="w-5 h-5 text-purple-600" /> Invita amigos, gana análisis
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Comparte tu enlace: cuando un amigo se registre con Google y complete su primer análisis,
                  <b> ambos recibís +{referral.reward_analyses} análisis gratis</b>.
                </p>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <code data-testid="referral-link" className="flex-1 min-w-[200px] text-xs bg-purple-50 border border-purple-100 rounded-xl px-4 py-3 text-purple-800 font-mono truncate">
                    {referral.link}
                  </code>
                  <button
                    data-testid="referral-copy"
                    onClick={copyReferralLink}
                    className="flex items-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all"
                  >
                    {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? "¡Copiado!" : "Copiar enlace"}
                  </button>
                </div>
                <div className="flex flex-wrap gap-4 text-sm">
                  <span data-testid="referral-progress" className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-2 text-gray-600">
                    Referidos: <b className="text-gray-900">{referral.referrals_count}/{referral.max_referrals}</b>
                  </span>
                  <span data-testid="referral-earned" className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-2 text-gray-600">
                    Análisis ganados: <b className="text-gray-900">{referral.referral_bonus_earned}</b>
                  </span>
                </div>
              </div>
            )}

            {/* Historial de pagos y facturas */}
            <div data-testid="account-payments" className="bg-white rounded-3xl shadow-xl p-8">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Receipt className="w-5 h-5 text-purple-600" /> Historial de facturas y pagos
              </h3>
              {!payments && <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />}
              {payments && payments.length === 0 && (
                <p data-testid="account-no-payments" className="text-sm text-gray-500">Aún no tienes pagos registrados.</p>
              )}
              {payments && payments.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-purple-50 text-purple-700 text-xs uppercase">
                        <th className="px-4 py-3 text-left rounded-l-lg">Fecha</th>
                        <th className="px-4 py-3 text-left">Concepto</th>
                        <th className="px-4 py-3 text-right">Importe</th>
                        <th className="px-4 py-3 text-center">Estado</th>
                        <th className="px-4 py-3 text-center rounded-r-lg">Factura</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p, i) => {
                        const badge = STATUS_BADGE[p.payment_status] || STATUS_BADGE.pending;
                        return (
                          <tr key={i} className="border-t border-gray-100">
                            <td className="px-4 py-3 text-gray-600">{fmtDate(p.created_at)}</td>
                            <td className="px-4 py-3 font-medium text-gray-800">
                              {p.concept}
                              <span className="block text-xs text-gray-400 font-mono">{p.reference}</span>
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-gray-800">{p.amount.toFixed(2)} $ {p.currency}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${badge.cls}`}>{badge.text}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {p.payment_status === "paid" ? (
                                <a
                                  href={`${API}/account/invoice/${p.session_id}`}
                                  data-testid={`invoice-download-${p.reference}`}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 transition-all"
                                >
                                  <Download className="w-3.5 h-3.5" /> Descargar factura
                                </a>
                              ) : (
                                <span className="text-xs text-gray-300">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
